import { isMobile } from '@resn/gozer-env';

/** @type {AudioContext} */
let audioCtx = null;
/** @type {GainNode} */
let masterGain = null;
let locked = true;
let unlockPromise;

const createFadeWorker = (subject, field = 'value') => {
    let target = subject[field];

    const blobUrl = URL.createObjectURL(
        new Blob(
            [
                `(${function worker() {
                    let intervalRef = 0;
                    self.addEventListener(
                        'message',
                        function (event) {
                            switch (event.data) {
                                case 'start':
                                    if (!intervalRef) {
                                        intervalRef = setInterval(function () {
                                            self.postMessage('tick');
                                        }, 30);
                                    }
                                    break;
                                case 'stop':
                                    clearInterval(intervalRef);
                                    intervalRef = 0;
                                    break;
                            }
                        },
                        false,
                    );
                }.toString()})()`,
            ],
            { type: 'application/javascript' },
        ),
    );

    const tickWorker = new Worker(blobUrl);
    URL.revokeObjectURL(blobUrl);
    const handleMessageWorker = () => {
        if (subject[field] < target) {
            subject[field] += 0.03;
            if (subject[field] >= target) {
                subject[field] = target;
                tickWorker.postMessage('stop');
            }
        } else if (subject[field] > target) {
            subject[field] -= 0.03;
            if (subject[field] <= target) {
                subject[field] = target;
                tickWorker.postMessage('stop');
            }
        } else {
            tickWorker.postMessage('stop');
        }
    };
    tickWorker.addEventListener('message', handleMessageWorker);

    return {
        get target() {
            return target;
        },
        set target(value) {
            target = value;
            tickWorker.postMessage('start');
        },
    };
};

const init = () => {
    if (audioCtx) {
        console.warn('Audio context has already been initialized.');
        return;
    }

    /** @type {AudioContext} */
    let audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Check if hack is necessary. Only occurs in iOS6+ devices
    // and only when you first boot the iPhone, or play a audio/video
    // with a different sample rate
    // if (/(iPhone|iPad)/i.test(navigator.userAgent) && audioContext.sampleRate !== 44100) {
    //     const buffer = audioContext.createBuffer(1, 1, 44100);
    //     const dummy = audioContext.createBufferSource();
    //     dummy.buffer = buffer;
    //     dummy.connect(audioContext.destination);
    //     dummy.start(0);
    //     dummy.disconnect();

    //     audioContext.close(); // dispose old context
    //     audioContext = new (window.AudioContext || window.webkitAudioContext)();
    // }

    const visibilityGain = audioContext.createGain();
    visibilityGain.connect(audioContext.destination);

    masterGain = audioContext.createGain();
    masterGain.connect(visibilityGain);

    unlockPromise = new Promise((resolve) => {
        const unlock = () => {
            audioCtx.resume();
            locked = false;
            window.removeEventListener('mousedown', unlock);
            window.removeEventListener('touchend', unlock);
            resolve();
        };

        window.addEventListener('mousedown', unlock);
        window.addEventListener('touchend', unlock);
    });

    const fadeWorker = createFadeWorker(visibilityGain.gain);
    const handleVisibilityChange = () => {
        if (document.hidden || (isMobile && !document.hasFocus())) {
            fadeWorker.target = 0;
            // visibilityGain.gain.value = 0;
        } else {
            fadeWorker.target = 1;
            // visibilityGain.gain.value = 1;
        }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('focus', handleVisibilityChange);
    document.addEventListener('blur', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    window.addEventListener('blur', handleVisibilityChange);

    audioCtx = audioContext;
};

const initCore = (core) => {
    init();
    Object.defineProperties(core, {
        context: {
            get() {
                return audioCtx;
            },
        },
        master: {
            get() {
                return masterGain;
            },
        },
        unlocked: {
            get() {
                return unlockPromise;
            },
        },
    });
};

const audioCore = {
    get locked() {
        return locked;
    },
    get init() {
        return initCore.bind(null, this);
    },
    get context() {
        initCore(this);
        return audioCtx;
    },
    get master() {
        initCore(this);
        return masterGain;
    },
    get unlocked() {
        initCore(this);
        return unlockPromise;
    },
};

export default audioCore;
